import React, {useMemo} from 'react';
import {Image} from 'antd-mobile';
import {tokenRoutes} from '@/app.service';
import {useSequentialModalContext} from '@/components/basic/sequential-modal/context';
import globalStore from '@/services/global.state';
import {goTo, toPriceStr} from '@/utils';

type RechargeBonusContentProps = {
  rewardAmount: number;
  openUrl: string;
  imageUrl: string;
};

const amountGlyphs = require('./assets/amount-glyphs.webp');

const AMOUNT_GLYPHS = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '.',
  ',',
  '₹',
];
const AMOUNT_GLYPH_HEIGHT_PX = 129;
const AMOUNT_GLYPH_DISPLAY_HEIGHT_REM = 21 / 16;
const AMOUNT_GLYPH_NORMAL_SOURCE_WIDTH_PX = 108;
const AMOUNT_GLYPH_NARROW_SOURCE_WIDTH_PX = 60;
const AMOUNT_GLYPH_DOT_DISPLAY_SOURCE_WIDTH_PX = 42;
const AMOUNT_GLYPH_DOT_SOURCE_OFFSET_X_PX = 9;
const AMOUNT_GLYPH_SCALE =
  AMOUNT_GLYPH_DISPLAY_HEIGHT_REM / AMOUNT_GLYPH_HEIGHT_PX;

const getAmountGlyphSourceWidth = (value: string) => {
  if (value === '.' || value === ',') {
    return AMOUNT_GLYPH_NARROW_SOURCE_WIDTH_PX;
  }
  return AMOUNT_GLYPH_NORMAL_SOURCE_WIDTH_PX;
};

const getAmountGlyphDisplaySourceWidth = (value: string) => {
  if (value === '.') {
    return AMOUNT_GLYPH_DOT_DISPLAY_SOURCE_WIDTH_PX;
  }
  return getAmountGlyphSourceWidth(value);
};

const getAmountGlyphDisplaySourceX = (value: string, sourceX: number) => {
  if (value === '.') {
    return sourceX + AMOUNT_GLYPH_DOT_SOURCE_OFFSET_X_PX;
  }
  return sourceX;
};

const getAmountGlyphSourceX = (glyphIndex: number) => {
  return AMOUNT_GLYPHS.slice(0, glyphIndex).reduce((total, value) => {
    return total + getAmountGlyphSourceWidth(value);
  }, 0);
};

const getAmountGlyphSpacingClass = (
  isFirst: boolean,
  previousValue: string,
  value: string,
) => {
  if (isFirst) {
    return '';
  }
  if (value === ',' || previousValue === ',') {
    return '-ml-[0.22rem]';
  }
  if (value === '.') {
    return '-ml-[0.16rem]';
  }
  if (previousValue === '.') {
    return '-ml-[0.24rem]';
  }
  return '-ml-[0.08rem]';
};

function AmountGlyph({
  isFirst,
  previousValue,
  value,
}: {
  isFirst: boolean;
  previousValue: string;
  value: string;
}) {
  if (value === ' ') {
    return <span className="inline-block w-[0.12rem]" />;
  }

  const glyphIndex = AMOUNT_GLYPHS.indexOf(value);
  if (glyphIndex < 0) {
    return null;
  }

  const sourceX = getAmountGlyphSourceX(glyphIndex);
  const displaySourceWidth = getAmountGlyphDisplaySourceWidth(value);
  const displaySourceX = getAmountGlyphDisplaySourceX(value, sourceX);
  const glyphStyle = {
    width: `${displaySourceWidth * AMOUNT_GLYPH_SCALE}rem`,
    backgroundImage: `url(${amountGlyphs})`,
    backgroundSize: `${1308 * AMOUNT_GLYPH_SCALE}rem 100%`,
    backgroundPosition: `${-displaySourceX * AMOUNT_GLYPH_SCALE}rem 0`,
  };

  if (value === '.') {
    return (
      <span
        aria-hidden="true"
        className={`inline-flex h-[1.3125rem] items-center justify-center align-middle translate-y-[0.18rem] ${getAmountGlyphSpacingClass(
          isFirst,
          previousValue,
          value,
        )}`}
        style={{
          width: `${displaySourceWidth * AMOUNT_GLYPH_SCALE}rem`,
        }}>
        <span
          className="inline-block h-[1.3125rem] origin-center scale-[0.72] bg-no-repeat"
          style={glyphStyle}
        />
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`inline-block h-[1.3125rem] bg-no-repeat align-middle ${
        value === ',' ? 'translate-y-[0.2rem]' : ''
      } ${getAmountGlyphSpacingClass(isFirst, previousValue, value)}`}
      style={glyphStyle}
    />
  );
}

function BonusAmount({amount}: {amount: number}) {
  const displayAmount = Math.min(amount, 99999);
  const priceText = useMemo(
    () => toPriceStr(displayAmount, {fixed: 0, maxFixed: 2, round: 'floor'}),
    [displayAmount],
  );

  return (
    <div
      aria-label={priceText}
      className="flex h-[1.5rem] max-w-full items-center justify-center overflow-hidden whitespace-nowrap">
      {priceText.split('').map((char, index) => (
        <AmountGlyph
          isFirst={index === 0}
          key={`${char}-${index}`}
          previousValue={priceText[index - 1]}
          value={char}
        />
      ))}
    </div>
  );
}

export default function RechargeBonusContent(props: RechargeBonusContentProps) {
  const {rewardAmount, openUrl, imageUrl} = props;
  const {goToNext, onClose} = useSequentialModalContext();

  const handleGoToReward = () => {
    onClose();
    if (!openUrl.startsWith('route:')) {
      return;
    }

    const [route] = openUrl.slice(6).split('?');
    const needToken = tokenRoutes.some(r => route.startsWith(r));
    if (!needToken || (needToken && globalStore.token)) {
      goTo(route);
      return;
    }

    goTo('Login', {
      sucessPage: route,
    });
  };

  return (
    <div className="relative w-[16.84375rem] max-w-[92vw] overflow-visible text-center">
      <Image
        className="pointer-events-none block w-full object-contain"
        fit="contain"
        src={imageUrl}
      />

      <div className="absolute bottom-[3.95rem] left-1/2 z-[4] w-full -translate-x-1/2 px-3">
        <BonusAmount amount={rewardAmount} />
      </div>

      <button
        aria-label="OK"
        className="absolute bottom-[1.15rem] left-1/2 z-[5] h-[2.55rem] w-[10.9rem] -translate-x-1/2 border-0 bg-transparent"
        onClick={goToNext}
        type="button"
      />

      <button
        aria-label="Check Details"
        className="absolute bottom-[-0.85rem] left-1/2 z-[5] h-[1.6rem] w-[8.8rem] -translate-x-1/2 border-0 bg-transparent"
        onClick={handleGoToReward}
        type="button"
      />
    </div>
  );
}
